import Fuse from 'fuse.js'

export interface FuzzyMatchResult {
  person: { id: string; name: string }
  score: number
  matchType: 'exact' | 'fuzzy'
}

/**
 * Find the best matching person for a given name using Fuse.js.
 *
 * Matching priority:
 * 1. Exact match (case-insensitive, full name or first name) — score: 1.0
 * 2. Fuse.js fuzzy match — score derived from Fuse score (0 = perfect, 1 = worst)
 *
 * @param mentionedName - The name mentioned in the text (e.g., "Bo", "Hanka")
 * @param availablePeople - List of available people to match against
 * @param threshold - Fuse.js threshold (0 = exact, 1 = match anything). Default: 0.4
 * @returns The best matching person or null if no good match found
 */
export function findBestMatch(
  mentionedName: string,
  availablePeople: { id: string; name: string }[],
  threshold: number = 0.4,
): FuzzyMatchResult | null {
  const normalizedMention = mentionedName.toLowerCase().trim()

  if (!normalizedMention || availablePeople.length === 0) {
    return null
  }

  // 1. Check for exact match first (case-insensitive, full name or first name)
  for (const person of availablePeople) {
    const normalizedName = person.name.toLowerCase().trim()
    const firstName = normalizedName.split(/[\s-]/)[0]

    if (
      normalizedName === normalizedMention ||
      firstName === normalizedMention
    ) {
      return { person, score: 1.0, matchType: 'exact' }
    }
  }

  // 2. Fuse.js fuzzy search
  const fuse = new Fuse(availablePeople, {
    keys: ['name'],
    threshold,
    includeScore: true,
  })

  const results = fuse.search(mentionedName)

  if (results.length === 0) {
    return null
  }

  const best = results[0]
  // Fuse score: 0 = perfect match, 1 = worst. Invert for our API.
  const score = 1 - (best.score ?? 0)

  return {
    person: best.item,
    score,
    matchType: 'fuzzy',
  }
}

/**
 * Find all matching people for a list of mentioned names.
 * Returns unique person IDs, avoiding duplicates.
 *
 * @param mentionedNames - Array of names mentioned in the text
 * @param availablePeople - List of available people to match against
 * @param threshold - Fuse.js threshold (default: 0.4)
 * @returns Array of matched person IDs
 */
export function findMatchingPeople(
  mentionedNames: string[],
  availablePeople: { id: string; name: string }[],
  threshold: number = 0.4,
): string[] {
  const matchedIds = new Set<string>()

  for (const name of mentionedNames) {
    const match = findBestMatch(name, availablePeople, threshold)
    if (match) {
      matchedIds.add(match.person.id)
    }
  }

  return Array.from(matchedIds)
}
